/* Per-route document titles + meta descriptions, plus the not-found strings.
 * Single source of truth consumed by useDocumentHead and NotFound.
 * Convention: `<Page> · Prinotes`; homepage is `Prinotes · <tagline>`;
 * About drops the suffix because its name already carries the brand.
 * meta.test.ts guards stem parity and the copy rules. */
import type { RouteStem } from '@/config/routes';
import type { Locale } from '@/strings/types';
import { META_AR } from './meta-ar';

export interface PageMeta {
  title: string;
  description: string;
}

export interface MetaCopy {
  pages: Record<RouteStem, PageMeta>;
  notFound: {
    title: string;
    heading: string;
    body: string;
    home: string;
    goBack: string;
  };
}

/* Single locale→META selector shared by useDocumentHead and NotFound.
 * (meta-ar imports only types from this file, so no runtime cycle.) */
export function metaFor(locale: Locale): MetaCopy {
  return locale === 'ar' ? META_AR : META_EN;
}

export const META_EN: MetaCopy = {
  pages: {
    '': {
      title: 'Prinotes · AI meeting notes in Arabic and English',
      description:
        'Prinotes uses AI to turn your meetings and conversations into organized notes and summaries, in Arabic and English, with your data kept private and secure.',
    },
    privacy: {
      title: 'Privacy Policy · Prinotes',
      description:
        'How Prinotes collects, uses, and protects your data, including your Google account data, audio recordings, and meeting notes.',
    },
    terms: {
      title: 'Terms of Service · Prinotes',
      description:
        'The terms that govern your use of Prinotes, including plans, payments, refunds, and acceptable use.',
    },
    subprocessors: {
      title: 'Sub-processors · Prinotes',
      description:
        'The third-party providers Prinotes relies on to deliver the service, and what data each one processes.',
    },
    about: {
      title: 'About Prinotes',
      description:
        'The story of Prinotes: an AI workspace for meeting notes, transcription, and summaries, built by a Saudi company for Arabic and English speakers.',
    },
    trust: {
      title: 'Trust & security · Prinotes',
      description:
        'How Prinotes protects your data: in-Kingdom data storage, encryption in transit and at rest, and AI that never trains on your content.',
    },
  },
  notFound: {
    title: 'Page not found · Prinotes',
    heading: 'This page couldn’t be found',
    body: 'Sorry, the page you’re looking for could not be found. Please check the link or return to the homepage.',
    home: 'Go home',
    goBack: 'Go back',
  },
};
